import { useState } from 'react';
import {
  NotProvided,
  Avatar,
  Alert,
  Badge,
  Button,
  Card,
  Filters,
  linkTextClasses,
  Modal,
  Page,
  PageHeader,
  Pagination,
  Table,
  type Action,
  type FilterFieldSlot,
} from '@evogard/ui';
import {
  businessDate,
  count,
  formatDate,
  formatInstant,
  type ClaimDepartment,
  type ClaimType,
} from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { DataCell } from '../DataCell.js';
import { ClaimForm } from './ClaimForm.js';
import { ClaimPanel } from './ClaimPanel.js';
import {
  CLAIM_TYPE_OPTIONS,
  DEPARTMENT_LABEL,
  DEPARTMENT_OPTIONS,
  labelOf,
  phaseLabel,
} from './labels.js';
import { useClaimList, useClaimMutations, useClaimPermissions } from './useClaims.js';

/**
 * Sinistros › Sinistros — o quadro de todos os sinistros da praça.
 *
 * Substitui o quadro do Aviso (decisão de 24/09): as 12 fases do aviso eram
 * roteamento entre pipes, e o sinistro nasce direto em Análise/Entrada. Esta é
 * a lista de onde se abre o sinistro do associado, e a ficha dele é de onde se
 * abre o do terceiro.
 *
 * ## A tabela é a de Comercial › Vendas
 *
 * Busca na barra (número, protocolo OPA ou código do veículo), o painel de
 * filtros (departamento, tipo e arquivados), as ações nas reticências, o
 * esqueleto do tamanho da página e a paginação do servidor.
 *
 * ## Quatro colunas, sem rolar para o lado
 *
 * Pedido do dono em 05/10/2026: a lista cabe na tela inteira, com as ações à
 * vista. Ficam o nome, a placa, a fase e a data do ocorrido. De quem é o
 * sinistro (associado, terceiro do #N, com terceiro, arquivado) vai na mesma
 * célula do nome, em selo pequeno; o departamento e a hora vão na dica; o
 * tipo, na segunda linha do telefone. O protocolo e o tipo continuam na ficha
 * e na busca — a barra acha pelo protocolo.
 *
 * ## Abrir o sinistro é num modal, por cima da lista
 *
 * Pedido do dono em 05/10/2026. O formulário é o mesmo `ClaimForm` — as etapas,
 * o trilho e o rodapé fixo —, só que dentro do `Modal`, e a lista continua
 * atrás, no filtro em que estava. Clicar no véu não fecha (são dezesseis
 * obrigatórios, e um clique fora apagaria tudo); o X, o Esc e "Cancelar"
 * fecham.
 *
 * ## Ver e corrigir é no painel do canto direito
 *
 * Também de 05/10/2026: "Abrir ficha" abre a ficha que já existia — o mesmo
 * miolo da página `ClaimScreen` — no `SidePanel`, e "Corrigir" abre ali o
 * `ClaimForm` de correção. Ver `ClaimPanel`.
 *
 * Arquivar pela linha pode ser RECUSADO — o do associado com sinistro de
 * terceiro ativo —, e a recusa aparece no alto da tela: o painel de ações
 * fecha antes de a ação rodar (mesmo desenho de `DepartmentsScreen`).
 */
export function ClaimsScreen() {
  const { data: context } = useSessionContext();
  const { canCreate, canEdit } = useClaimPermissions();
  const { update } = useClaimMutations();

  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [department, setDepartment] = useState<ClaimDepartment | ''>('');
  const [type, setType] = useState<ClaimType | ''>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [creating, setCreating] = useState(false);

  const query = useClaimList({
    search: term,
    currentDepartment: department || undefined,
    type: type || undefined,
    includeArchived,
    page,
    perPage,
  });
  const result = query.data;
  const hasFilter =
    term.trim() !== '' || department !== '' || type !== '' || includeArchived;

  /* A ficha abre no painel do canto direito, por cima da lista (`ClaimPanel`). */
  const [openedId, setOpenedId] = useState<number | null>(null);
  const openFile = (id: number) => setOpenedId(id);

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'department',
      label: 'Departamento',
      placeholder: 'Todos',
      options: DEPARTMENT_OPTIONS,
    },
    {
      type: 'selecao',
      name: 'type',
      label: 'Tipo',
      placeholder: 'Todos',
      options: [
        { value: 'associado', label: 'Do associado' },
        { value: 'terceiro', label: 'Do terceiro' },
      ],
    },
    {
      type: 'selecao',
      name: 'archived',
      label: 'Arquivados',
      placeholder: 'Esconder',
      options: [{ value: 'sim', label: 'Mostrar também os arquivados' }],
    },
  ];

  return (
    <Page measure="cheia">
      <PageHeader
        title="Sinistros"
        actions={
          canCreate && (
            <Button icon="mais" onClick={() => setCreating(true)}>
              Abrir sinistro
            </Button>
          )
        }
      >
        Os sinistros de {context?.currentTenant.nome}. Todo sinistro nasce na Análise; o
        do terceiro se abre pela ficha do sinistro do associado.
      </PageHeader>

      {canCreate && (
        <Modal
          open={creating}
          onOpenChange={setCreating}
          size="xl"
          dismissOnOutsideClick={false}
          title="Abrir sinistro do associado"
          descricao="O que se sabe agora. O sinistro nasce na Análise, na fase Entrada."
        >
          <ClaimForm
            mode={{ kind: 'associado' }}
            layer="modal"
            onSaved={(claim) => {
              setCreating(false);
              openFile(claim.id);
            }}
            onCancel={() => setCreating(false)}
          />
        </Modal>
      )}

      <ClaimPanel
        claimId={openedId}
        onClose={() => setOpenedId(null)}
        onOpenClaim={setOpenedId}
      />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {update.error && <Alert tone="erro">{update.error.message}</Alert>}

      {(!query.isError || result) && (
        <Card
          title={
            result === undefined
              ? 'Carregando…'
              : count(result.total, 'sinistro', 'sinistros')
          }
          descricao={includeArchived ? 'Ativos e arquivados.' : 'Só os ativos.'}
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar sinistros',
              placeholder: 'Qualquer dado do sinistro: nome, placa, relato, protocolo…',
              value: search,
              onChange: (value) => {
                setSearch(value);
                setPage(1);
              },
            }}
            toolbar={
              <Filters
                values={{ department, type, archived: includeArchived ? 'sim' : '' }}
                fields={fields}
                onApply={(values) => {
                  setDepartment(String(values.department ?? '') as ClaimDepartment | '');
                  setType(String(values.type ?? '') as ClaimType | '');
                  setIncludeArchived(values.archived === 'sim');
                  setPage(1);
                }}
              />
            }
            label="Sinistros desta base"
            items={result?.items ?? []}
            loading={query.isLoading}
            skeletonRows={perPage}
            rowKey={(c) => String(c.id)}
            rowTitle={(c) => `Sinistro #${c.id}`}
            rowSubtitle={(c) => labelOf(CLAIM_TYPE_OPTIONS, c.claimType)}
            rowIcon="escudo"
            rowActions={(c): Action[] => [
              { label: 'Abrir ficha', icon: 'seta-direita', run: () => openFile(c.id) },
              ...(canEdit
                ? [
                    {
                      label: c.archivedAt ? 'Reativar' : 'Arquivar',
                      icon: (c.archivedAt ? 'atualizar' : 'bloqueado') as
                        'atualizar' | 'bloqueado',
                      run: () =>
                        update.mutate({ id: c.id, body: { archived: !c.archivedAt } }),
                    },
                  ]
                : []),
            ]}
            empty={
              hasFilter
                ? 'Nenhum sinistro com esses filtros. Isso é sobre o recorte, não sobre a base.'
                : 'Nenhum sinistro aberto nesta base ainda. O sinistro do associado se abre por "Abrir sinistro"; o do terceiro, pela ficha do associado.'
            }
            columns={[
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                cell: (c) => {
                  /* O associado vem do espelho do SGA, e pode não vir: com a
                     esteira atrasada a linha continua, sem o nome. */
                  const name =
                    c.type === 'terceiro' ? c.thirdParty?.name : c.vehicle?.memberName;
                  return (
                    <div
                      className="flex min-w-0 items-center gap-2"
                      title={
                        c.type === 'terceiro'
                          ? `Sinistro #${c.id}, do terceiro envolvido no sinistro #${c.parentClaimId} do associado`
                          : c.involvesThirdParty
                            ? `Sinistro #${c.id}, do associado, com terceiro envolvido`
                            : `Sinistro #${c.id}, do associado`
                      }
                    >
                      <span className="shrink-0 text-xs font-semibold text-texto-suave tabular-nums">
                        #{c.id}
                      </span>
                      <Avatar nome={name ?? null} size="sm" />
                      <span className="min-w-0 truncate">{name ?? <NotProvided />}</span>
                      {/* De quem é o sinistro, apertado ao lado do nome: o
                          associado e o terceiro têm análise e reparo
                          independentes, e o terceiro diz de qual é. */}
                      {c.type === 'terceiro' ? (
                        <Badge tone="neutro">
                          Terceiro do{' '}
                          <button
                            type="button"
                            className={linkTextClasses('tabular-nums')}
                            onClick={() => c.parentClaimId && openFile(c.parentClaimId)}
                            aria-label={`Abrir o sinistro #${c.parentClaimId}, do associado`}
                          >
                            #{c.parentClaimId}
                          </button>
                        </Badge>
                      ) : (
                        <Badge tone="neutro">
                          {c.involvesThirdParty ? 'Associado · c/ terceiro' : 'Associado'}
                        </Badge>
                      )}
                      {c.archivedAt && <Badge tone="neutro">Arquivado</Badge>}
                    </div>
                  );
                },
              },
              {
                title: 'Placa',
                width: '9rem',
                cell: (c) => {
                  if (c.type === 'terceiro') {
                    return c.thirdParty?.plate ? (
                      <span
                        className="font-mono tabular-nums"
                        title={c.thirdParty.model ?? undefined}
                      >
                        {c.thirdParty.plate}
                      </span>
                    ) : (
                      <NotProvided>sem veículo</NotProvided>
                    );
                  }
                  const model = c.vehicle
                    ? [c.vehicle.brand, c.vehicle.model].filter(Boolean).join(' ') ||
                      `Código ${c.vehicleCode} no SGA`
                    : `Código ${c.vehicleCode} no SGA — ainda fora do cadastro da casa`;
                  return c.vehicle?.plate ? (
                    <span className="font-mono tabular-nums" title={model}>
                      {c.vehicle.plate}
                    </span>
                  ) : (
                    <span className="tabular-nums" title={model}>
                      código {c.vehicleCode}
                    </span>
                  );
                },
              },
              {
                title: 'Fase',
                width: '13rem',
                /* Só sinistro em processo chega aqui: o encerrado é de
                   Encerrados (22/10/2026). */
                cell: (c) => (
                  <DataCell
                    icon="quadro"
                    hint={`${DEPARTMENT_LABEL[c.currentDepartment]} · ${phaseLabel(c.currentDepartment, c.currentPhase)}`}
                  >
                    <span className="truncate">
                      {phaseLabel(c.currentDepartment, c.currentPhase)}
                    </span>
                  </DataCell>
                ),
              },
              {
                title: 'Ocorrido',
                width: '8rem',
                cell: (c) => (
                  <span className="tabular-nums" title={formatInstant(c.occurredAt)}>
                    {formatDate(businessDate(c.occurredAt))}
                  </span>
                ),
              },
            ]}
          />
          {result && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={setPage}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
              label="sinistros"
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
